import { colourDistance, contrastRequirement, parseHexColour, readableForeground, } from "./contrast.js";
/**
 * The platform's own colours, used when a tenant supplies nothing usable.
 *
 * Neutral on purpose. A fallback that looked like a brand would be a brand
 * nobody chose; this reads as the product's own chrome, which is what it is.
 */
const PLATFORM = {
    primary: "#1f2933",
    secondary: "#3e4c59",
    accent: "#0b6bcb",
};
/**
 * The danger colour a tenant colour must not be mistaken for.
 *
 * Kept here rather than in a palette file because the collision check is the
 * only thing that needs it, and a semantic colour that drifts away from the one
 * being checked against would make the check quietly meaningless.
 */
const DANGER = { red: 0xc0, green: 0x28, blue: 0x28 };
/**
 * Below this sRGB distance, two colours are close enough that a glance will not
 * separate them. Chosen to catch the reds and red-oranges without flagging
 * every warm colour; it is a threshold for review, not a verdict.
 */
const DANGER_COLLISION_DISTANCE = 60;
function resolveRole(role, supplied, fallback, warnings) {
    const parsed = supplied === undefined ? undefined : parseHexColour(supplied);
    if (supplied !== undefined && parsed === undefined) {
        warnings.push({
            code: "malformed_colour",
            role,
            message: `The ${role} colour "${supplied}" is not a hex colour; the platform colour is used instead`,
        });
    }
    const usedFallback = parsed === undefined;
    const colour = parsed ?? parseHexColour(fallback);
    const background = usedFallback ? fallback : normaliseHex(colour);
    const choice = readableForeground(colour, { requirement: contrastRequirement.bodyText });
    if (!usedFallback && !choice.meetsRequirement) {
        warnings.push({
            code: `${role}_unreadable`,
            role,
            message: `No platform text colour reaches 4.5:1 on the ${role} colour (best is ${choice.ratio.toFixed(2)}:1), so it must not carry text`,
            measurement: Number(choice.ratio.toFixed(2)),
        });
    }
    if (!usedFallback && role !== "secondary") {
        const distance = colourDistance(colour, DANGER);
        if (distance < DANGER_COLLISION_DISTANCE) {
            warnings.push({
                code: `${role}_near_danger`,
                role,
                message: `The ${role} colour is close to the danger colour (distance ${distance.toFixed(0)}), so a branded control and a destructive one may not be distinguishable`,
                measurement: Number(distance.toFixed(0)),
            });
        }
    }
    return {
        resolved: {
            background,
            foreground: choice.foreground,
            safeForText: choice.meetsRequirement,
            contrast: Number(choice.ratio.toFixed(2)),
        },
        usedFallback,
    };
}
function normaliseHex(colour) {
    const part = (value) => value.toString(16).padStart(2, "0");
    return `#${part(colour.red)}${part(colour.green)}${part(colour.blue)}`;
}
export function resolveBrand(input) {
    const warnings = [];
    const primary = resolveRole("primary", input.primary, PLATFORM.primary, warnings);
    const secondary = resolveRole("secondary", input.secondary, PLATFORM.secondary, warnings);
    const accent = resolveRole("accent", input.accent, PLATFORM.accent, warnings);
    return {
        primary: primary.resolved,
        secondary: secondary.resolved,
        accent: accent.resolved,
        warnings,
        usedFallbackOnly: primary.usedFallback && secondary.usedFallback && accent.usedFallback,
    };
}
/**
 * The resolved brand as CSS custom properties.
 *
 * Custom properties rather than a stylesheet per tenant, because the brand
 * arrives after the bundle does. `--brand-primary-on` is the computed
 * foreground; `--brand-primary-text-safe` is 1 or 0 so a stylesheet can decide
 * without asking JavaScript again.
 */
export function brandCustomProperties(brand) {
    const roles = { primary: brand.primary, secondary: brand.secondary, accent: brand.accent };
    const properties = {};
    for (const [name, role] of Object.entries(roles)) {
        properties[`--brand-${name}`] = role.background;
        properties[`--brand-${name}-on`] = role.foreground;
        properties[`--brand-${name}-text-safe`] = role.safeForText ? "1" : "0";
    }
    return properties;
}
//# sourceMappingURL=brand.js.map